import { Locales } from './language'

export enum MonetaryUnit {
  KRW = 'KRW',
  JPY = 'JPY',
  USD = 'USD',
  THB = 'THB',
}

export type MonetaryUnitString = keyof typeof MonetaryUnit

export function addComma(price: number): string | number {
  if (!price) {
    return price
  }
  return typeof price === 'string' ? Number.parseInt(price).toLocaleString() : price.toLocaleString()
}

/**
 * 숫자에 콤마를 찍고, 국가에 따라 화폐 단위를 붙여서 반환합니다.
 * @param currency 화폐 단위
 * @param locale 국가 코드
 * @param price 숫자
 * @returns 화폐 단위가 붙은 숫자 ex. 1,000원, ￦1,000, 1,000円
 */
export function getPriceWithMonetaryUnit({
  currency,
  locale,
  price,
}: {
  currency: MonetaryUnitString
  locale: Locales
  price: number
}): string {
  if (currency === 'JPY') {
    return `¥${addComma(price)}`
  }
  if (currency === 'KRW') {
    return locale === Locales.KO_KR ? `${addComma(price)}원` : `￦${addComma(price)}`
  }
  if (currency === 'USD') {
    return `$${addComma(price)}`
  }
  if (currency === 'THB') {
    return `฿${addComma(price)}`
  }
  return `${addComma(price)}`
}

/**
 * 할인 비율을 계산합니다.
 * @param originalCost 원래 가격
 * @param discountedCost 할인 가격
 * @param decimalPlaces 소숫점 자리수
 * @returns 할인 비율
 */
export const getDiscountRate = (originalCost: number, discountedCost: number, decimalPlaces = 0): number => {
  if (originalCost === 0) {
    return 0
  }
  const discountRate = ((originalCost - discountedCost) / originalCost) * 100
  return Math.floor(discountRate * 10 ** decimalPlaces) / 10 ** decimalPlaces
}

/**
 * 할인 정보를 반환합니다. 법규에 따라 할인 비율이 50% 이상일 경우 custom한 text를 반환합니다.
 * @param originalCost 원래 가격
 * @param discountedCost 할인 가격
 * @param specialPriceText 50% 이상일 경우 사용할 텍스트
 * @returns 할인 정보 ex. 40%, 42%, 40%, 50% 이상일 경우 텍스트 반환
 */
export const getDiscountInfo = ({
  originalCost,
  discountedCost,
  specialPriceText,
  decimalPlaces = 0,
}: {
  originalCost: number
  discountedCost: number
  specialPriceText?: string
  decimalPlaces?: number
}): string | null => {
  const discountRate = getDiscountRate(originalCost, discountedCost, decimalPlaces)
  if (discountRate <= 0) {
    return null
  }
  if (discountRate >= 50) {
    return specialPriceText || ''
  }
  return `${discountRate.toFixed(decimalPlaces)}%`
}
